import { RootState } from "app/reducer";
import { useDispatch, useSelector } from "react-redux";
import { setPaymentState } from "slices/store";
import { isBool } from "util/helper";
import { useEffect, useState } from "react";
import { renderHalfSwitch } from "../_components/payment-functions";
import { renderHalfTxtInput, renderHalfTxtSelec } from "./payment-functions";
import { SelectOption } from "../../../../../components/form";
import { FormattedMessage, useIntl } from "react-intl";
import h from "../helper";
import { Prompt } from "react-router-dom";
import { Button, Input } from "antd";
import config from "config";
import { generateCloverToken } from "services/store";

// currently, there is a bug that the component will be mounted twice in setting page, so we use a flag to prevent the second time
let didHandleOnboard = false;

const CloverPaymentSettings = (): JSX.Element => {
    const intl = useIntl();
    const dispatch = useDispatch();
    const setState = (name: string, value: any) => dispatch(setPaymentState({ name, value }));
    const switchChange = (name: string, value: any) => dispatch(setPaymentState({ name, value: isBool(!value) }));
    const paymentOptions = useSelector((state: RootState) => state.store?.storePaymentOptions);
    const [merchantIdInClover, setMerchantIdInClover] = useState("");

    const {
        clover_enabled,
        clover_is_debug,
        clover_payment_action,
        clover_merch_id,
        clover_descriptor_name,
        clover_descriptor_contact,
        clover_payment_credit_card,
        clover_payment_google_pay,
        clover_payment_apple_pay,
        clover_contingency_3d_secure,
    } = paymentOptions;

    const payAction: SelectOption[] = [
        { value: "Authorization", label: intl.formatMessage({ id: "authorization" }) },
        { value: "Sale", label: intl.formatMessage({ id: "sale" }) },
    ];

    const modeOptions: SelectOption[] = [
        { value: 1, label: "Sandbox" },
        { value: 0, label: "Production" },
    ];

    useEffect(() => {
        const normalizedParams = location.search.slice(1).replace(/\?/g, "&");
        const searchParams = new URLSearchParams(normalizedParams);
        // if public merchant clicked the public onboard button on public onboard page, it will have from_public_onboard=true
        const fromPublicOnboard = searchParams.get("from_public_onboard") === "true";
        const fromCardpresent = searchParams.get("from_cardpresent") === "true";
        if (fromPublicOnboard || fromCardpresent) {
            // if it is from public onboard page or from cardpresent tab, we do not handle the onboard process in this component
            // for this cases, the redirect logic are in src/pages/settings/pages/payment-options/index.tsx
            return;
        }

        const merchantId = searchParams.get("merchant_id");
        // 'is_live' only exists in clover onboarding return url
        const isLive = searchParams.get("is_live");
        const isDebug = isLive === "true" ? false : true;
        if (merchantId != null) {
            dispatch(setPaymentState({ name: "clover_enabled", value: true }));
            dispatch(setPaymentState({ name: "clover_is_debug", value: Number(isDebug) }));
            dispatch(setPaymentState({ name: "clover_payment_action", value: "Authorization" }));
            dispatch(setPaymentState({ name: "clover_merch_id", value: merchantId }));
            dispatch(setPaymentState({ name: "clover_descriptor_name", value: "" }));
            dispatch(setPaymentState({ name: "clover_descriptor_contact", value: "" }));
            dispatch(setPaymentState({ name: "clover_payment_credit_card", value: false }));
            dispatch(setPaymentState({ name: "clover_payment_google_pay", value: false }));
            dispatch(setPaymentState({ name: "clover_payment_apple_pay", value: false }));
            dispatch(setPaymentState({ name: "clover_contingency_3d_secure", value: false }));
            setMerchantIdInClover(merchantId);

            if (didHandleOnboard) {
                // remove query parameters from the URL to avoid refreshing the page with the same parameters
                removeQueryParams();
                return;
            }
            didHandleOnboard = true;

            const searchParams = new URLSearchParams(location.search);
            const authorizationCode = searchParams.get("code");
            dispatch(
                generateCloverToken(
                    {
                        // change string boolean isLive to boolean
                        is_live: isLive === "true",
                        authorization_code: authorizationCode,
                        // for clover iframe integration, the PAKMS key is needed https://docs.clover.com/dev/reference/getapikey
                        is_clover: true,
                        merchant_id: merchantId,
                    },
                    false
                )
            );
        }
    }, [location.search]);

    const removeQueryParams = () => {
        const url = new URL(window.location.href);
        // Keep only the 'tab' parameter
        url.search = "?tab=clover";
        // Update the URL without redirecting or reloading
        window.history.replaceState({}, "", String(url));
    };

    const OnboardHandler = async () => {
        const CLOVER_BASE_URL = clover_is_debug ? config.CLOVER.API_BASE_URL.SANDBOX : config.CLOVER.API_BASE_URL.LIVE;
        const CLOVER_CLIENT_ID = clover_is_debug
            ? config.CLOVER.ECOMMERCE_CLIENT_ID.SANDBOX
            : config.CLOVER.ECOMMERCE_CLIENT_ID.LIVE;
        const isLive = String(!clover_is_debug);

        // const RIDIRECT_URL = `http://localhost:3000/settings/paymentOptions?tab=clover&is_live=${isLive}`;
        const RIDIRECT_URL = `${config.ROOT_URL}settings/paymentOptions?tab=clover&is_live=${isLive}`;

        // Construct the URL dynamically by replacing placeholders
        const url = `${CLOVER_BASE_URL}/oauth/v2/authorize?client_id=${CLOVER_CLIENT_ID}&redirect_uri=${encodeURIComponent(
            RIDIRECT_URL
        )}`;
        window.open(url, "_blank");
    };

    const OnboardButton = () => (
        <div className="payment-title-container">
            <FormattedMessage id={"clover_setting_tip"} />
            {renderHalfTxtSelec("mode", "clover_is_debug", Number(clover_is_debug), modeOptions, setState, true)}
            <div className="payment-subtitle mr-3 mb-0">
                <Button
                    type="primary"
                    size="large"
                    onClick={() => {
                        OnboardHandler();
                    }}
                    className="save-button-handle-onboard"
                >
                    <FormattedMessage id="connect_clover_button" />
                </Button>
            </div>
        </div>
    );

    const DisconnectHandler = () => {
        dispatch(setPaymentState({ name: "clover_enabled", value: false }));
        dispatch(setPaymentState({ name: "clover_is_debug", value: false }));
        dispatch(setPaymentState({ name: "clover_payment_action", value: "Authorization" }));
        dispatch(setPaymentState({ name: "clover_merch_id", value: "" }));
        dispatch(setPaymentState({ name: "clover_descriptor_name", value: "" }));
        dispatch(setPaymentState({ name: "clover_descriptor_contact", value: "" }));
        dispatch(setPaymentState({ name: "clover_payment_credit_card", value: false }));
        dispatch(setPaymentState({ name: "clover_payment_google_pay", value: false }));
        dispatch(setPaymentState({ name: "clover_payment_apple_pay", value: false }));
        dispatch(setPaymentState({ name: "clover_contingency_3d_secure", value: false }));
    };

    const DisconnectButton = () => (
        <div className="payment-title-container">
            <FormattedMessage id={"clover_setting_tip"} />
            <div className="halfish-size d-flex flex-column justify-content-start align-items-start">
                <div className="payment-subtitle">
                    <FormattedMessage id={"mode"} />
                </div>
                <Input
                    type={"text"}
                    className="custom-input-textfield halfish-size-input"
                    value={modeOptions.find((option) => option.value === Number(clover_is_debug))?.label || "N/A"}
                    readOnly
                />
            </div>

            <div className="payment-subtitle mr-3 mb-0">
                <Button
                    type="primary"
                    size="large"
                    onClick={() => {
                        DisconnectHandler();
                    }}
                    className="save-button-handle-onboard"
                >
                    <FormattedMessage id="disconnect_clover_button" />
                </Button>
            </div>
        </div>
    );

    return (
        <div className="white-single-block d-flex flex-column justify-content-start align-items-start">
            <Prompt
                when={h.checkRequiredFilled(paymentOptions, "ppcp_setting")}
                message={intl.formatMessage({ id: "payment_option_disabled_unless_required" })}
            />
            {/* {renderTop("clover", "clover_setting_tip", "clover_enabled", clover_enabled, switchChange)} */}
            {clover_enabled == undefined || clover_enabled == false ? OnboardButton() : DisconnectButton()}

            {clover_enabled ? (
                <div className="payment-input-container">
                    {renderHalfTxtInput(
                        "merchant_name",
                        "clover_descriptor_name",
                        clover_descriptor_name,
                        setState,
                        false,
                        true
                    )}
                    {renderHalfTxtInput(
                        "merchant_contact_number",
                        "clover_descriptor_contact",
                        clover_descriptor_contact,
                        setState,
                        false,
                        true
                    )}
                    {renderHalfTxtInput(
                        "merchant_id",
                        "clover_merch_id",
                        merchantIdInClover === "" ? clover_merch_id : merchantIdInClover,
                        setState,
                        false,
                        true,
                        undefined,
                        undefined,
                        true
                    )}
                    {renderHalfTxtSelec(
                        "payment_action",
                        "clover_payment_action",
                        clover_payment_action,
                        payAction,
                        setState,
                        true
                    )}
                    <div className="clover-present-switch-row">
                        {renderHalfSwitch(
                            "google_pay",
                            "clover_payment_google_pay",
                            isBool(clover_payment_google_pay),
                            switchChange
                        )}
                        {renderHalfSwitch(
                            "apple_pay",
                            "clover_payment_apple_pay",
                            isBool(clover_payment_apple_pay),
                            switchChange
                        )}
                        {renderHalfSwitch(
                            "credit_card2",
                            "clover_payment_credit_card",
                            isBool(clover_payment_credit_card),
                            switchChange
                        )}
                        {renderHalfSwitch(
                            "contingency_3d_secure",
                            "clover_contingency_3d_secure",
                            isBool(clover_contingency_3d_secure),
                            switchChange
                        )}
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default CloverPaymentSettings;
